import { FC, MouseEvent, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';

import { IcDotSpread, IcPopUp, IcGPS } from '@/assets/images';
import { Button } from '@/components/TailwindComponents';
import { EmergencyContactSideModal } from '@/layouts/TabLayout/MasterNames/components/EmergencyContactSIdeModal';
import { MasterNameFormData } from '@/layouts/TabLayout/MasterNames/components/MasterNameForm';
import { EmergencyContactsTableColumns } from '@/layouts/TabLayout/MasterNames/data';
import { IEmergencyContact, IMasterNamePerson } from '@/models';
import { getAddressLocation } from '@/services';
import { ConfirmTypes, useToastStore } from '@/store';
import { colors } from '@/theme/variables';
import { formatPhoneNumber } from '@/utils/pattern';

interface EmergencyContactMenuState {
  anchorEl: null | HTMLElement;
  selectedId?: string;
}

interface EmergencyContactsTableProps {
  emergencyContacts: IEmergencyContact[];
  disabled?: boolean;
}

interface EmergencyContactsTableRowProps {
  row: IEmergencyContact;
  handleOpenMenu: (e: MouseEvent<HTMLButtonElement>, id: string) => void;
  openActionMenu: boolean;
  disabled?: boolean;
}

const Dot = styled(Box)(() => ({
  width: 0,
  height: 0,
  display: 'inline',
  marginLeft: '7px',
  position: 'relative',
  '::before': {
    content: '""',
    position: 'absolute',
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: colors.error.main,
    left: '50%',
    top: '50%',
    transform: 'translate(-50%, -50%)',
  },
}));

function openInGoogleMaps(address: string) {
  const encodedAddress = encodeURIComponent(address);
  window.open(
    `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`,
    '_blank',
  );
}

const EmergencyContactsTableRow: FC<EmergencyContactsTableRowProps> = ({
  row,
  handleOpenMenu,
  openActionMenu,
  disabled = false,
}) => {
  const navigate = useNavigate();

  const contactPerson = row.emergencyContact as IMasterNamePerson;

  const relationship = row.relationship?.relationship ?? 'n/a';

  const personPhoneNumber =
    contactPerson.phoneNumbers && contactPerson.phoneNumbers.length > 0
      ? formatPhoneNumber(contactPerson.phoneNumbers[0].phoneNumber)
      : 'n/a';
  const hasMultiplePhoneNumbers =
    contactPerson.phoneNumbers && contactPerson.phoneNumbers.length > 1;

  const personEmail =
    contactPerson.emails && contactPerson.emails.length > 0
      ? typeof contactPerson.emails[0] === 'string'
        ? contactPerson.emails[0]
        : (contactPerson.emails[0] as { email: string }).email
      : 'n/a';
  const hasMultipleEmails =
    contactPerson.emails && contactPerson.emails.length > 1;

  const personAddress =
    contactPerson.addresses && contactPerson.addresses.length > 0
      ? contactPerson.addresses[0]
      : undefined;
  const hasMultipleAddresses =
    contactPerson.addresses && contactPerson.addresses.length > 1;

  const addressText = getAddressLocation(personAddress) || 'n/a';
  const hasValidAddress = addressText !== 'n/a';

  return (
    <TableRow sx={{ borderTop: `1px solid  ${colors.grey[10]}!important` }}>
      <TableCell>{contactPerson.lastName}</TableCell>
      <TableCell>{contactPerson.firstName}</TableCell>
      <TableCell>{contactPerson.middleName ?? ''}</TableCell>
      <TableCell>{relationship}</TableCell>
      <TableCell>
        {personPhoneNumber} {hasMultiplePhoneNumbers && <Dot />}
      </TableCell>
      <TableCell>
        {personEmail} {hasMultipleEmails && <Dot />}
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span>
            {addressText} {hasMultipleAddresses && <Dot />}
          </span>
          {hasValidAddress && (
            <Tooltip title="Open in Google Maps" placement="top">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openInGoogleMaps(addressText);
                }}
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '6px',
                  border: '1px solid #CFD6DA',
                  backgroundColor: '#FFFFFF',
                  color: '#3E5A6A',

                  transition: 'all 0.2s ease',
                  pointerEvents: 'auto',
                }}
              >
                <IcGPS />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell>{personAddress?.subAddress ?? 'n/a'}</TableCell>
      <TableCell>{row.relationship?.isPrimary ? 'Yes' : ''}</TableCell>
      <TableCell>
        <Tooltip title="Open in new window">
          <IconButton
            onClick={() =>
              navigate(
                `/master-index/persons/${contactPerson._id}/view/information`,
              )
            }
          >
            <IcPopUp height={24} width={14} />
          </IconButton>
        </Tooltip>
        {!disabled && (
          <IconButton
            id="action-button"
            aria-controls={openActionMenu ? 'action-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={openActionMenu ? 'true' : undefined}
            onClick={(e) =>
              handleOpenMenu(
                e,
                (row.emergencyContact as IMasterNamePerson)._id as string,
              )
            }
          >
            <IcDotSpread />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );
};

export const EmergencyContactsTable: FC<EmergencyContactsTableProps> = ({
  emergencyContacts,
  disabled = false,
}) => {
  const { setValue } = useFormContext<MasterNameFormData>();

  const { updateToast } = useToastStore();

  const [menuState, setMenuState] = useState<EmergencyContactMenuState>({
    anchorEl: null,
  });

  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [currentEmergencyContact, setCurrentEmergencyContact] = useState<
    IEmergencyContact | undefined
  >(undefined);

  const handleOpenMenu = (e: MouseEvent<HTMLButtonElement>, id: string) => {
    e.stopPropagation();
    setMenuState({ anchorEl: e.currentTarget, selectedId: id });
  };

  const handleCloseMenu = () => {
    setMenuState({ anchorEl: null });
  };

  function renderRow(row: IEmergencyContact) {
    return (
      <EmergencyContactsTableRow
        row={row}
        handleOpenMenu={handleOpenMenu}
        openActionMenu={openActionMenu}
        disabled={disabled}
      />
    );
  }

  const onEdit = () => {
    const currentId = menuState.selectedId;
    if (!currentId) {
      return;
    }

    const foundEmergencyContact = emergencyContacts.find(
      (ec) => (ec.emergencyContact as IMasterNamePerson)._id === currentId,
    );
    if (!foundEmergencyContact) {
      return;
    }

    setCurrentEmergencyContact(foundEmergencyContact);
    setDrawerOpen(true);
  };

  const onDelete = () => {
    const currentId = menuState.selectedId;
    if (!currentId) {
      return;
    }

    setValue(
      'emergencyContacts',
      emergencyContacts.filter(
        (ec) => (ec.emergencyContact as IMasterNamePerson)?._id !== currentId,
      ),
    );

    updateToast({
      type: ConfirmTypes.SUCCESS,
      open: true,
      message:
        'Emergency contact successfully deleted. Remember to save the person record to finalize the update.',
    });
  };

  const onMarkEmergencyContactAsPrimary = () => {
    const currentId = menuState.selectedId;
    if (!currentId) {
      return;
    }

    setValue(
      'emergencyContacts',
      emergencyContacts.map((ec) => ({
        ...ec,
        relationship: {
          ...ec.relationship,
          isPrimary:
            (ec.emergencyContact as IMasterNamePerson)._id === currentId,
        },
      })),
    );

    updateToast({
      type: ConfirmTypes.SUCCESS,
      open: true,
      message:
        'Emergency contact successfully marked as primary. Remember to save the person record to finalize the update.',
    });
  };

  const handleAddEmergencyContact = () => {
    setCurrentEmergencyContact(undefined);
    setDrawerOpen(true);
  };

  const openActionMenu = Boolean(menuState.anchorEl);

  return (
    <Stack spacing={2} flex={1}>
      <TableContainer sx={{ px: 1 }}>
        <Table
          sx={{
            borderCollapse: 'collapse',
            minWidth: 400,
          }}
          aria-label="custom pagination table"
          stickyHeader
        >
          <TableHead>
            <TableRow sx={{ '& > :first-child': { maxWidth: 20 } }}>
              {EmergencyContactsTableColumns.map((title) => (
                <TableCell
                  key={title}
                  sx={{
                    fontWeight: 'bold',
                    backgroundColor: 'background.paper',
                  }}
                >
                  {title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {emergencyContacts.map((row) => renderRow(row))}
            {emergencyContacts.length < 1 && (
              <TableRow>
                <TableCell
                  align="center"
                  colSpan={EmergencyContactsTableColumns.length}
                  sx={{
                    color: 'grey',
                    borderBottomWidth: 0,
                  }}
                >
                  No Data
                </TableCell>
              </TableRow>
            )}
          </TableBody>
          {!disabled && (
            <Button
              color="error"
              type="button"
              size="sm"
              variant="secondary"
              className="text-sm w-fit"
              onClick={handleAddEmergencyContact}
            >
              + Add new emergency contact
            </Button>
          )}
        </Table>

        <Menu
          id="action-menu"
          anchorEl={menuState.anchorEl}
          open={openActionMenu}
          onClose={handleCloseMenu}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <MenuItem onClick={onMarkEmergencyContactAsPrimary}>
            Set as primary
          </MenuItem>
          <MenuItem onClick={onEdit}>Edit</MenuItem>
          <MenuItem onClick={onDelete}>Delete</MenuItem>
        </Menu>
      </TableContainer>
      {isDrawerOpen && (
        <EmergencyContactSideModal
          isDrawerOpen={isDrawerOpen}
          setDrawerOpen={setDrawerOpen}
          emergencyContact={currentEmergencyContact}
        />
      )}
    </Stack>
  );
};
